import { useTranslation } from "@sagot/i18n";
import { AppLink, Checkbox, Icon } from "@sagot/ui";
import { formatBytes } from "../../deploy-knowledge/shared/format";
import { PiiChip } from "../../deploy-knowledge/knowledge/access-badges";
import { detailHref } from "../../deploy-knowledge/knowledge/source-row";
import { sourceIcon, typeOf } from "../../deploy-knowledge/knowledge/source-model";
import { SourceStatus } from "../../deploy-knowledge/knowledge/source-status";
import type { SourceView } from "../shared/types";
import { AccessSelect } from "./access-select";
import type { AccessPreset } from "./agent-knowledge-model";

const K = "build.agentKnowledge";

export interface AgentKnowledgeRowProps {
  source: SourceView;
  linked: boolean;
  /** Ticking sources needs permission to edit the agent. */
  canLink: boolean;
  /** Changing the source's access needs `knowledge.edit`. */
  canEditAccess: boolean;
  busy: boolean;
  workspaceSlug: string;
  agentId: string;
  /** Id of the paragraph that explains a locked control. */
  lockNoteId: string;
  onToggle: (source: SourceView, on: boolean) => void;
  onPick: (source: SourceView, preset: AccessPreset) => void;
}

function AppliesNote({ count }: { count: number }) {
  const { t } = useTranslation();
  return <p className="mt-1 text-[11px] text-fg-subtle">{count > 0 ? t(`${K}.appliesAll`, { count }) : t(`${K}.appliesNone`)}</p>;
}

/** One workspace source: use it in this agent, see its status and decide who may read it. */
export function AgentKnowledgeRow({ source, linked, canLink, canEditAccess, busy, workspaceSlug, agentId, lockNoteId, onToggle, onPick }: AgentKnowledgeRowProps) {
  const { t } = useTranslation();
  const tile = sourceIcon(source);
  const href = detailHref(workspaceSlug, source.id, agentId);
  return (
    <tr data-testid="agent-source-row">
      <td className="w-12">
        <Checkbox aria-label={t(`${K}.useIn`, { name: source.title })} checked={linked} disabled={!canLink || busy} onCheckedChange={(next) => { onToggle(source, next === true); }} />
      </td>
      <td>
        <div className="flex items-center gap-2.5">
          <span className={`inline-flex size-8 shrink-0 items-center justify-center rounded-md ${tile.tile}`}>
            <Icon name={tile.icon} className="size-4" />
          </span>
          <div className="min-w-0">
            <AppLink href={href} className="block max-w-64 truncate font-semibold text-fg hover:text-primary-ink">
              {source.title}
            </AppLink>
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-fg-subtle">
              <span>{t(`deployKnowledge.knowledge.types.${typeOf(source)}`)}</span>
              {source.size_bytes ? <span>· {formatBytes(source.size_bytes)}</span> : null}
              <PiiChip source={source} />
            </div>
          </div>
        </div>
      </td>
      <td>
        <SourceStatus source={source} detailsHref={href} />
      </td>
      <td className="text-right tabular-nums">{source.chunk_count}</td>
      <td>
        <AccessSelect name={source.title} audiences={source.audiences} disabled={!canEditAccess} describedBy={canEditAccess ? undefined : lockNoteId} onPick={(preset) => { onPick(source, preset); }} />
        <AppliesNote count={source.agent_count} />
      </td>
    </tr>
  );
}
